import { useEffect } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { useGetTenantQuery } from '@crew/apis/tenantSetting/tenantSettingApis'
import { ContractPlan, Roles } from '@crew/enums/app'
import { assertNever } from '@crew/utils'
import { useGetMyAccountQuery, useGetUserQuery } from '@crew/apis/user/userApis'

import { useAppSelector } from 'states/hooks'
import { FullstarTag, FullstarTagAdmin, FullstarTagPlan } from 'enums/app'

const FULLSTAR_PRODUCT_UUID = process.env.REACT_APP_FULLSTAR_PRODUCT_UUID

const planToFullstarTag = (
  plan: ContractPlan | null
): FullstarTagPlan | null => {
  switch (plan) {
    case ContractPlan.Free:
      return FullstarTagPlan.Free
    case ContractPlan.Standard:
      return FullstarTagPlan.Standard
    case ContractPlan.Professional:
      return FullstarTagPlan.Professional
    case null:
      return null
    default:
      assertNever(plan, `Unknown plan: ${plan}`)
  }
}

const roleCodeToFullstarTag = (
  roleCode: string
): FullstarTagAdmin | undefined => {
  switch (roleCode) {
    case Roles.SysTenantAdmin.value:
      return FullstarTagAdmin.TenantAdmin
    case Roles.SysAdmin.value:
      return FullstarTagAdmin.Admin
    default:
      console.warn(`Unknown role code: ${roleCode}`)
      return undefined
  }
}

/**
 * Fullstarを有効化するコンポーネント
 * @returns
 */
export const Fullstar = () => {
  // fullstarに渡す情報を準備する。
  // 全体に単純な変換や代入なのでuseMemoしない

  // ログインユーザー情報
  const loggedInUser = useAppSelector((state) => state.app.loggedInUser)
  const { data: getMyAccountResponse } = useGetMyAccountQuery(
    // このAPIは引数がないため、ログイン前に呼び出したり、別アカウントでログインした場合にキャッシュされてリロードされないことがある。
    // それを避けるため、ログアウト状態の場合はskipTokenを使用し、ログインの度にロードし直しさせる
    loggedInUser?.id ? undefined : skipToken
  )

  // ユーザーのメールアドレス
  const userProfileEmail = getMyAccountResponse?.myAccount?.email ?? undefined

  // ユーザーの詳細情報
  const { data: getUserResponse, isSuccess: isSuccessGetUser } =
    useGetUserQuery(loggedInUser?.id ? { userId: loggedInUser.id } : skipToken)
  const roleCode = getUserResponse?.user?.role?.roleCode ?? undefined
  const roleTag = roleCode && roleCodeToFullstarTag(roleCode)

  // テナント情報
  const { data: getTenantResponse } = useGetTenantQuery()
  // 契約中のプラン
  const currentPlan = useAppSelector((state) => state.app.currentPlan)
  const planTag = planToFullstarTag(currentPlan)

  // 最終的なタグセット
  const tagList = [planTag, roleTag].filter(Boolean) as FullstarTag[] // 空やnull、undefinedを除外する

  // fullstarに渡す値を準備する。useEffectでreactiveに反応させるため、また把握しやすいよう、フラットに置く
  const user_id = loggedInUser?.id
  const company_name = getTenantResponse?.tenant?.name
  const user_name = loggedInUser?.displayName
  const user_email = userProfileEmail
  const plan_name = planTag
  const tags = tagList.join(',') // タグはカンマ区切りの文字列で渡す

  // fullstarに引き渡す情報に更新があった場合、fullstarの初期化処理を実行する
  useEffect(() => {
    if (!FULLSTAR_PRODUCT_UUID) {
      console.info(
        '[Fullstar] Fullstar product UUID is not set for this environment.'
      )
      return
    }

    // 必要な情報がない場合は初期化しない
    if (
      !user_id ||
      !company_name ||
      !user_name ||
      !user_email ||
      !plan_name ||
      !isSuccessGetUser
    ) {
      console.info(
        `[Fullstar] Fullstar initialization skipped due to missing user information. user_id: ${user_id}, company_name: ${company_name}, user_name: ${user_name}, user_email: ${user_email}, plan_name: ${plan_name}, isSuccessGetUser: ${isSuccessGetUser}`
      )
      return
    }

    // fullstarを読み込み、初期化する。
    // 読込・初期化処理は「埋め込みコード」としてFullstarのマスタ管理→プロダクト管理から取得可能。
    // https://partner.fullstar10.com/products
    // Crewに適用しているのはVer.11.4.0
    // https://manual.fullstar10.com/releasenote/1140.html
    //
    // <script type="application/javascript" src="https://view.fullstar10.com/js/review.js?spaFlg=true&spaTime=2000&customSpaFlg=false&skipReplaceState=false"></script>
    // <script>
    //   fs.clear()
    //   fs.show('b65fc299-96d5-4709-8376-748f406cc908', 'ユーザーのUUID', {
    //     company_name: 'ユーザーの会社名',
    //     user_name: 'ユーザーの氏名',
    //     user_email: 'ユーザーのEmail',
    //     plan_name: 'ユーザーのプラン名',
    //     tags: '',
    //   })
    //   fs.tracking()
    //   fs.survey()
    //   fs.guide()
    //   fs.checklist()
    //   fs.tooltip()
    //   fs.validation_assist()
    // </script>

    // review.jsは読み込んだ後に、自動的にFullstar側の初期化処理が走ってしまうため、<head>の<script>で読み込むことができない。
    // そのため、必要な情報が揃った時点で動的に<script>タグを生成して読み込む。
    // 読込が成功した後で初期化処理を行うため、onloadイベントを利用する。
    //
    // なおspaTimeは、7/24にFullstar側と打合せした際に口頭で「500ミリ秒程度にするのが良い」旨指定頂いたため500に設定している。
    // https://us02web.zoom.us/j/5914887630
    // ガイドの動作が遅い件の対応で何度か調整したが、現在は500ミリとしている
    // https://tmc.crewworks.net/tasks/01K73ATCASZE9RXM2Z0DT8DG00/workHistory?messageId=01KAACCXAGCJXE89KV2F758207
    console.info('[Fullstar] Loading Fullstar script...')
    const script = document.createElement('script')
    script.src =
      'https://view.fullstar10.com/js/review.js?spaFlg=true&spaTime=500&customSpaFlg=false&skipReplaceState=false'
    script.async = true

    script.onerror = () => {
      console.error('[Fullstar] Failed to load Fullstar script.')
    }
    script.onload = () => {
      // <script>の読み込み完了、Fullstarに初期化情報を渡す
      console.info('[Fullstar] Fullstar script loaded.')

      // ユーザ情報の設定
      if (!window.fs) {
        console.error(
          '[Fullstar] Fullstar script is not properly loaded. window.fs is undefined.'
        )
        return
      }

      // 埋め込みコードに則って初期化する。埋め込みコードの詳細は前述の通り
      const params: fsShowParams = {
        company_name,
        user_name,
        user_email,
        plan_name,
        tags,
      }

      window.fs.clear()
      window.fs.show(FULLSTAR_PRODUCT_UUID, user_id, params)
      window.fs.tracking()
      window.fs.survey()
      window.fs.guide()
      window.fs.checklist()
      window.fs.tooltip()
      window.fs.validation_assist()

      console.info('[Fullstar] Fullstar initialized successfully.', params)
    }

    // 読込開始
    document.body.appendChild(script)

    return () => {
      // クリーンアップ: <script>タグを削除し、fullstarの情報をクリアする
      document.body.removeChild(script)
      if (window.fs) {
        window.fs.clear()
        console.info('[Fullstar] Fullstar instance cleared.')

        delete window.fs
      }
    }
  }, [
    company_name,
    plan_name,
    tags,
    user_email,
    user_id,
    user_name,
    isSuccessGetUser,
  ])

  return null
}
